import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import PortalLayout from './PortalLayout';
import PortalStatusPill from './PortalStatusPill';
import { portalCardSx } from './portalStyles';
import PortalProjectService from '../../services/PortalProjectService';
import portalAxiosInstance from '../../services/portalAxiosInstance';

// ─── Status colour maps ───────────────────────────────────────────────────────

const PROJECT_STATUS_META = {
    PENDING:   { label: 'Pending',   color: '#9E9E9E' },
    ONGOING:   { label: 'Ongoing',   color: '#1976d2' },
    COMPLETED: { label: 'Completed', color: '#2e7d32' },
    CANCELLED: { label: 'Cancelled', color: '#c62828' },
};

const QUOTE_STATUS_META = {
    SENT:       { label: 'Sent',       color: '#1976d2' },
    ACCEPTED:   { label: 'Accepted',   color: '#2e7d32' },
    REJECTED:   { label: 'Rejected',   color: '#c62828' },
    DRAFT:      { label: 'Draft',      color: '#9E9E9E' },
};

const CONTRACT_STATUS_META = {
    SIGNING:           { label: 'Awaiting Signature', color: '#F4A261' },
    CHANGES_REQUESTED: { label: 'Changes Requested',  color: '#E0C454' },
    ACTIVE:            { label: 'Active',              color: '#2EC4B6' },
    TERMINATED:        { label: 'Terminated',          color: '#B0B0B0' },
    DRAFT:             { label: 'Draft',               color: '#9E9E9E' },
};

const INVOICE_STATUS_META = {
    PENDING:  { label: 'Pending',  color: '#F4A261' },
    PAID:     { label: 'Paid',     color: '#2e7d32' },
    OVERDUE:  { label: 'Overdue',  color: '#c62828' },
    CANCELLED:{ label: 'Cancelled',color: '#9E9E9E' },
};

function StatusChip({ status, metaMap }) {
    const meta = (metaMap || {})[status];
    return <PortalStatusPill status={status} label={meta ? meta.label : undefined} />;
}

function SectionHeader({ title }) {
    return (
        <Box sx={{ borderLeft: '3px solid #427898', pl: 1.5, py: 0.25, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#1a2332', lineHeight: 1.2 }}>
                {title}
            </Typography>
        </Box>
    );
}

function FieldRow({ label, value }) {
    return (
        <Box sx={{ py: 1.25, borderBottom: '1px solid #f1f5f9' }}>
            <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1, mb: 0.4 }}>
                {label}
            </Typography>
            <Typography variant="body2" sx={{ color: '#1a2332', fontWeight: 500 }}>
                {value || '—'}
            </Typography>
        </Box>
    );
}

function formatDate(d) {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatAmount(val) {
    if (val == null) return '—';
    return 'CHF ' + Number(val).toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatDateTime(d) {
    if (!d) return '';
    return new Date(d).toLocaleString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
}

// ─── Notes thread ─────────────────────────────────────────────────────────────

function NoteItem({ note, projectId, onUpdated }) {
    const [editing, setEditing] = useState(false);
    const [editValue, setEditValue] = useState(note.content || '');
    const [saving, setSaving] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);

    const canEdit = !note.deleted && note.authorType === 'CUSTOMER';

    const handleSave = async () => {
        if (!editValue.trim()) return;
        setSaving(true);
        try {
            await PortalProjectService.editNote(projectId, note.id, editValue.trim());
            setEditing(false);
            onUpdated();
        } catch (e) {
            // ignore — parent will not refresh on error
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        setConfirmDelete(false);
        try {
            await PortalProjectService.deleteNote(projectId, note.id);
            onUpdated();
        } catch (e) {
            // ignore
        }
    };

    const isCrm = note.authorType === 'CRM_USER';

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: isCrm ? 'row' : 'row-reverse',
                gap: 1.5,
                mb: 2,
            }}
        >
            {/* Avatar-like indicator */}
            <Box
                sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    backgroundColor: isCrm ? '#427898' : '#2e7d32',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    mt: 0.5,
                }}
            >
                <Typography sx={{ color: 'white', fontSize: 12, fontWeight: 700 }}>
                    {isCrm ? 'T' : 'Y'}
                </Typography>
            </Box>

            <Box sx={{ flex: 1, maxWidth: '80%' }}>
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography variant="caption" fontWeight={700} sx={{ color: '#475569' }}>
                        {isCrm ? 'Your team' : 'You'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                        {formatDateTime(note.createdAt)}
                    </Typography>
                    {note.editedAt && !note.deleted && (
                        <Typography variant="caption" sx={{ color: '#94a3b8', fontStyle: 'italic' }}>
                            (edited)
                        </Typography>
                    )}
                </Box>

                {/* Bubble */}
                <Paper
                    elevation={0}
                    sx={{
                        p: 1.5,
                        backgroundColor: isCrm ? '#f1f5f9' : '#e8f4e8',
                        border: '1px solid',
                        borderColor: isCrm ? '#e2e8f0' : '#c8e6c9',
                        borderRadius: 2,
                        position: 'relative',
                    }}
                >
                    {note.deleted ? (
                        <Typography
                            variant="body2"
                            sx={{ color: '#94a3b8', fontStyle: 'italic' }}
                        >
                            [deleted]
                        </Typography>
                    ) : editing ? (
                        <Box>
                            <TextField
                                fullWidth
                                multiline
                                minRows={2}
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                size="small"
                                autoFocus
                                sx={{ mb: 1 }}
                            />
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                    size="small"
                                    variant="contained"
                                    disabled={saving || !editValue.trim()}
                                    onClick={handleSave}
                                    sx={{ backgroundColor: '#427898', textTransform: 'none', fontSize: 12 }}
                                    startIcon={saving ? <CircularProgress size={12} /> : <CheckIcon />}
                                >
                                    Save
                                </Button>
                                <Button
                                    size="small"
                                    onClick={() => { setEditing(false); setEditValue(note.content); }}
                                    sx={{ textTransform: 'none', fontSize: 12, color: '#64748b' }}
                                    startIcon={<CloseIcon />}
                                >
                                    Cancel
                                </Button>
                            </Box>
                        </Box>
                    ) : (
                        <Typography variant="body2" sx={{ color: '#1a2332', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {note.content}
                        </Typography>
                    )}

                    {/* Edit/Delete actions */}
                    {canEdit && !editing && (
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, justifyContent: 'flex-end' }}>
                            <Tooltip title="Edit">
                                <IconButton size="small" onClick={() => { setEditValue(note.content); setEditing(true); }}>
                                    <EditIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                                <IconButton size="small" onClick={() => setConfirmDelete(true)}>
                                    <DeleteOutlineIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    )}
                </Paper>
            </Box>

            {/* Delete confirm dialog */}
            <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Delete Note</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        Are you sure you want to delete this note? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDelete(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
                    <Button onClick={handleDelete} color="error" variant="contained" sx={{ textTransform: 'none' }}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function PortalProjectDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [project, setProject] = useState(null);
    const [notes, setNotes] = useState([]);
    const [loadingProject, setLoadingProject] = useState(true);
    const [loadingNotes, setLoadingNotes] = useState(true);
    const [error, setError] = useState(null);
    const [newNoteContent, setNewNoteContent] = useState('');
    const [addingNote, setAddingNote] = useState(false);
    const notesEndRef = useRef(null);

    const fetchProject = useCallback(() => {
        PortalProjectService.getProject(id)
            .then(setProject)
            .catch(() => setError('Failed to load project details.'))
            .finally(() => setLoadingProject(false));
    }, [id]);

    const fetchNotes = useCallback(() => {
        PortalProjectService.getNotes(id)
            .then(data => setNotes(data || []))
            .catch(() => {}) // notes are non-critical
            .finally(() => setLoadingNotes(false));
    }, [id]);

    useEffect(() => {
        fetchProject();
        fetchNotes();
    }, [fetchProject, fetchNotes]);

    const handleAddNote = async () => {
        if (!newNoteContent.trim()) return;
        setAddingNote(true);
        try {
            await PortalProjectService.addNote(id, newNoteContent.trim());
            setNewNoteContent('');
            fetchNotes();
            setTimeout(() => notesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
        } catch (e) {
            // ignore
        } finally {
            setAddingNote(false);
        }
    };

    const handleDownloadInvoice = async (invoiceId, displayName) => {
        try {
            const response = await portalAxiosInstance.get(`/api/portal/invoices/${invoiceId}/pdf`, { responseType: 'blob' });
            const url = URL.createObjectURL(response.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = (displayName || 'invoice') + '.pdf';
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            // ignore
        }
    };

    if (loadingProject) {
        return (
            <PortalLayout>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
                    <CircularProgress sx={{ color: '#427898' }} />
                </Box>
            </PortalLayout>
        );
    }

    if (error || !project) {
        return (
            <PortalLayout>
                <Alert severity="error">{error || 'Project not found.'}</Alert>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/portal/projects')}
                    sx={{ mt: 2, textTransform: 'none' }}
                >
                    Back to Projects
                </Button>
            </PortalLayout>
        );
    }

    return (
        <PortalLayout>
            {/* ── Sticky header ── */}
            <Box
                sx={{
                    ...portalCardSx,
                    position: 'sticky',
                    top: 12,
                    zIndex: 10,
                    px: 2.5,
                    py: 1.25,
                    mb: 3,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                }}
            >
                <Button
                    size="small"
                    startIcon={<ArrowBackIcon sx={{ fontSize: '16px !important' }} />}
                    onClick={() => navigate('/portal/projects')}
                    sx={{ color: '#64748B', fontWeight: 500, textTransform: 'none', fontSize: 13, px: 1, minWidth: 0, flexShrink: 0, '&:hover': { bgcolor: '#F1F5F9' } }}
                >
                    Projects
                </Button>
                <Divider orientation="vertical" flexItem sx={{ height: 16, alignSelf: 'center' }} />
                <Typography noWrap sx={{ fontWeight: 700, fontSize: 15, color: '#0F172A' }}>
                    {project.displayName || project.name}
                </Typography>
                <StatusChip status={project.status} metaMap={PROJECT_STATUS_META} />
            </Box>

            {/* Section 1: Project Info */}
            <Paper elevation={0} sx={{ ...portalCardSx, p: 3, mb: 3 }}>
                <SectionHeader title="Project Details" />
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 0 }}>
                    <FieldRow label="Project Name" value={project.name} />
                    <FieldRow label="Status" value={
                        <StatusChip status={project.status} metaMap={PROJECT_STATUS_META} />
                    } />
                    <FieldRow label="Start Date" value={formatDate(project.startDate)} />
                    <FieldRow label="End Date" value={formatDate(project.endDate)} />
                </Box>
            </Paper>

            {/* Section 2: Quote */}
            {project.linkedQuoteSummary && (
                <Paper elevation={0} sx={{ ...portalCardSx, p: 3, mb: 3 }}>
                    <SectionHeader title="Quote" />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="body2" fontWeight={600} sx={{ color: '#1a2332' }}>
                            {project.linkedQuoteSummary.displayName || `Quote #${project.linkedQuoteSummary.id}`}
                        </Typography>
                        <StatusChip status={project.linkedQuoteSummary.status} metaMap={QUOTE_STATUS_META} />
                        <Button
                            component={Link}
                            to={`/portal/quotes/${project.linkedQuoteSummary.id}`}
                            size="small"
                            variant="outlined"
                            sx={{
                                borderColor: '#427898',
                                color: '#427898',
                                textTransform: 'none',
                                fontWeight: 600,
                                ml: 'auto',
                                '&:hover': { backgroundColor: '#42789810' },
                            }}
                        >
                            View Quote
                        </Button>
                    </Box>
                </Paper>
            )}

            {/* Section 3: Contract */}
            {project.linkedContractSummary && (
                <Paper elevation={0} sx={{ ...portalCardSx, p: 3, mb: 3 }}>
                    <SectionHeader title="Contract" />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="body2" fontWeight={600} sx={{ color: '#1a2332' }}>
                            {project.linkedContractSummary.displayName || `Contract #${project.linkedContractSummary.id}`}
                        </Typography>
                        <StatusChip status={project.linkedContractSummary.status} metaMap={CONTRACT_STATUS_META} />
                        <Button
                            component={Link}
                            to={`/portal/contracts/${project.linkedContractSummary.id}`}
                            size="small"
                            variant="outlined"
                            sx={{
                                borderColor: '#427898',
                                color: '#427898',
                                textTransform: 'none',
                                fontWeight: 600,
                                ml: 'auto',
                                '&:hover': { backgroundColor: '#42789810' },
                            }}
                        >
                            View Contract
                        </Button>
                    </Box>
                </Paper>
            )}

            {/* Section 4: Invoices */}
            <Paper elevation={0} sx={{ ...portalCardSx, p: 3, mb: 3 }}>
                <SectionHeader title="Invoices" />
                {(!project.publishedInvoices || project.publishedInvoices.length === 0) ? (
                    <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                        No invoices published yet.
                    </Typography>
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead sx={{ backgroundColor: '#f8fafc' }}>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 700, color: '#64748b', fontSize: 12 }}>Invoice</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: '#64748b', fontSize: 12 }}>Date</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: '#64748b', fontSize: 12 }}>Amount</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: '#64748b', fontSize: 12 }}>Status</TableCell>
                                    <TableCell />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {project.publishedInvoices.map((inv) => (
                                    <TableRow key={inv.id} hover>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={600} sx={{ color: '#1a2332' }}>
                                                {inv.displayName || `Invoice #${inv.id}`}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ color: '#475569' }}>
                                                {formatDate(inv.invoiceDate)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={600} sx={{ color: '#1a2332' }}>
                                                {formatAmount(inv.totalAmount)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <StatusChip status={inv.status} metaMap={INVOICE_STATUS_META} />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Download PDF">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleDownloadInvoice(inv.id, inv.displayName)}
                                                    sx={{ color: '#427898' }}
                                                >
                                                    <DownloadIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>

            {/* Section 5: Reports */}
            {project.signedReports && project.signedReports.length > 0 && (
                <Paper elevation={0} sx={{ ...portalCardSx, p: 3, mb: 3 }}>
                    <SectionHeader title="Service Reports" />
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    <TableCell sx={{ fontWeight: 700, color: '#64748b', fontSize: 12 }}>Report</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: '#64748b', fontSize: 12 }}>Confirmed</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: '#64748b', fontSize: 12 }}>Signed by</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: '#64748b', fontSize: 12 }}>Method</TableCell>
                                    <TableCell />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {project.signedReports.map((rep) => (
                                    <TableRow key={rep.id} hover>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={600} sx={{ color: '#1a2332' }}>
                                                {rep.displayName || `Report #${rep.id}`}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ color: '#475569' }}>
                                                {formatDate(rep.signedAt || rep.reportDate)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ color: '#475569' }}>
                                                {rep.signerName || '—'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            {rep.signatureMethod && (
                                                <Chip
                                                    size="small"
                                                    label={rep.signatureMethod === 'ON_SITE' ? 'On-site' : 'Remote'}
                                                    sx={{
                                                        fontSize: 11, fontWeight: 600, height: 20,
                                                        bgcolor: (rep.signatureMethod === 'ON_SITE' ? '#427898' : '#7c3aed') + '18',
                                                        color: rep.signatureMethod === 'ON_SITE' ? '#427898' : '#7c3aed',
                                                    }}
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Download PDF">
                                                <IconButton
                                                    size="small"
                                                    onClick={async () => {
                                                        try {
                                                            const resp = await portalAxiosInstance.get(
                                                                `/api/portal/reports/${rep.id}/download`,
                                                                { responseType: 'blob' }
                                                            );
                                                            const url = URL.createObjectURL(resp.data);
                                                            const a = document.createElement('a');
                                                            a.href = url;
                                                            a.download = (rep.displayName || 'report') + '.pdf';
                                                            a.click();
                                                            URL.revokeObjectURL(url);
                                                        } catch {}
                                                    }}
                                                    sx={{ color: '#427898' }}
                                                >
                                                    <DownloadIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}

            {/* Section 6: Notes */}
            <Paper elevation={0} sx={{ ...portalCardSx, p: 3 }}>
                <SectionHeader title="Notes" />

                {/* Notes thread */}
                <Box sx={{ mb: 2, maxHeight: 480, overflowY: 'auto', pr: 1 }}>
                    {loadingNotes ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                            <CircularProgress size={24} sx={{ color: '#427898' }} />
                        </Box>
                    ) : notes.length === 0 ? (
                        <Typography variant="body2" sx={{ color: '#94a3b8', textAlign: 'center', py: 3 }}>
                            No notes yet. Be the first to leave a message.
                        </Typography>
                    ) : (
                        notes.map((note) => (
                            <NoteItem
                                key={note.id}
                                note={note}
                                projectId={id}
                                onUpdated={fetchNotes}
                            />
                        ))
                    )}
                    <div ref={notesEndRef} />
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Add note */}
                <Box>
                    <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        placeholder="Write a note or question..."
                        value={newNoteContent}
                        onChange={(e) => setNewNoteContent(e.target.value)}
                        size="small"
                        sx={{ mb: 1.5 }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && e.ctrlKey) handleAddNote();
                        }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            variant="contained"
                            disabled={addingNote || !newNoteContent.trim()}
                            onClick={handleAddNote}
                            sx={{
                                backgroundColor: '#427898',
                                textTransform: 'none',
                                fontWeight: 600,
                                '&:hover': { backgroundColor: '#356380' },
                            }}
                            startIcon={addingNote ? <CircularProgress size={14} color="inherit" /> : null}
                        >
                            Add Note
                        </Button>
                    </Box>
                </Box>
            </Paper>
        </PortalLayout>
    );
}
